import {
  Form,
  SubmitFormButton,
  TextField
} from '@calendly/form'
import { I18n } from '@calendly/i18n'
import { Button } from '@calendly/ui/components/button'
import { ContextualBanner } from '@calendly/ui/components/contextual-banner'
import { GridItem } from '@calendly/ui/components/grid'
import { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { StepControls } from 'features/organization_settings/shared/step'
import { Row, Hint, CertificateLabel, JustifiedGrid } from 'features/organization_settings/shared/styles'
import { orgSessionSettingsSelector } from 'selectors/current_organization'

import { CertificateFileInput } from '../certificate_file_input'
import { testSamlSettingsSelector, testSamlSessionSettingsSelector, testSamlSessionErrorsSelector } from '../selectors'
import { SessionDurationSelectField } from '../session_duration_select_field'
import { saveTestSamlSettings } from '../thunks'
import { buildValidationSchema } from '../validation_schema'
import { STEPS } from './constants'

export const TestIdpInformation = ({ onClose }) => {
  const formikRef = useRef()
  const dispatch = useDispatch()
  const testSamlSettings = useSelector(testSamlSettingsSelector)
  const testSessionTimeoutIn = useSelector(testSamlSessionSettingsSelector)
  const sessionTimeoutIn = useSelector(orgSessionSettingsSelector)
  const testSamlErrors = useSelector(testSamlSessionErrorsSelector)
  const handleSubmit = (values) => dispatch(saveTestSamlSettings({ settings: values, nextStep: STEPS.CONNECTION }))

  const validationSchema = useMemo(buildValidationSchema, [])

  useEffect(() => {
    if (!formikRef.current) return
    if (!testSamlErrors) return

    const { setFieldError } = formikRef.current

    Object.entries(testSamlErrors).forEach(([field, error]) => {
      setFieldError(field, Array.isArray(error) ? error[0] : error)
    })
  }, [testSamlErrors])


  const initialValues = {
    entity_id: '',
    sso_url: '',
    certificate: '',
    ...testSamlSettings,
    session_settings: {
      session_timeout_in: testSessionTimeoutIn || sessionTimeoutIn
    },
  }

  return (
    <Form
      innerRef={formikRef}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      <ContextualBanner spacingBottom={24}>
        {I18n.t('publisher.organization_settings.test_saml_settings.test_connection_warning')}
      </ContextualBanner>
      <Row>
        <TextField
          name="entity_id"
          label={I18n.t('publisher.organization_settings.test_saml_settings.idp_information.entity_id')}
          placeholder={I18n.t('publisher.organization_settings.test_saml_settings.idp_information.entity_id_placeholder')}
          id="Test Entity ID"
        />
      </Row>
      <Row>
        <TextField
          name="sso_url"
          label={I18n.t('publisher.organization_settings.test_saml_settings.idp_information.sso_url')}
          placeholder={I18n.t('publisher.organization_settings.test_saml_settings.idp_information.sso_url_placeholder')}
          id="Test Request URL"
        />
      </Row>
      <Row>
        <JustifiedGrid>
          <GridItem>
            <label htmlFor="certificate">
              <CertificateLabel>
                <div>
                  {I18n.t('publisher.organization_settings.test_saml_settings.idp_information.certificate')}
                  <Hint>
                    {I18n.t('publisher.organization_settings.test_saml_settings.idp_information.certificate_hint')}
                  </Hint>
                </div>
              </CertificateLabel>
            </label>
          </GridItem>
          <GridItem>
            <CertificateFileInput name="certificate" />
          </GridItem>
        </JustifiedGrid>
        <TextField
          name="certificate"
          type="textarea"
          placeholder={I18n.t('publisher.organization_settings.test_saml_settings.idp_information.certificate_placeholder')}
          id="Test X.509 certificate"
        />
      </Row>
      <Row>
        <SessionDurationSelectField
          name="session_settings.session_timeout_in"
          label={I18n.t('publisher.organization_settings.test_saml_settings.idp_information.session_duration')}
          ariaLabel="sessionDurationTestLabel"
        />
      </Row>
      <StepControls>
        <Button
          decoration="secondary-outline"
          onClick={onClose}
        >
          {I18n.t('publisher.action.cancel')}
        </Button>
        <SubmitFormButton>{I18n.t('publisher.action.continue')}</SubmitFormButton>
      </StepControls>
    </Form>
  )
}
